import React, { useState, useEffect, useRef } from 'react';
import ThinkDreamSection from '../components/ThinkDreamSection';
import UIScript from '../components/UIScript';
import CTASection from '../components/CTASection';
import '../styles/ProductWrite.css';

const ProductWriting = () => {
  const [storyMapDots, setStoryMapDots] = useState([]);
  const storyMapLineRef = useRef(null);
  const purpleIndicatorRef = useRef(null);
  const [activeTab, setActiveTab] = useState('script');

  useEffect(() => {
    if (storyMapDots.length > 0) {
      return;
    }

    const lineElement = storyMapLineRef.current;
    if (!lineElement) {
      return;
    }

    let frameId;
    const computeDots = () => {
      const width = lineElement.offsetWidth;
      if (!width) {
        frameId = requestAnimationFrame(computeDots);
        return;
      }

      const count = 32;
      let seed = 1037;
      const random = () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
      };

      const positions = [0];
      for (let i = 1; i < count; i += 1) {
        positions.push(random() * width);
      }

      positions.sort((a, b) => a - b);
      positions[0] = 0;

      setStoryMapDots(
        positions.map((left, index) => ({
          id: index,
          left,
        }))
      );
    };

    computeDots();

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [storyMapDots.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (purpleIndicatorRef.current) {
        const indicator = purpleIndicatorRef.current;
        const section = indicator.closest('.writing-section');

        if (!section) return;

        const sectionTop = section.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const sectionHeight = section.offsetHeight;

        // Only start parallax when section enters viewport
        if (sectionTop < windowHeight && sectionTop > -sectionHeight) {
          // Calculate scroll progress within the section
          const scrollProgress = Math.max(0, (windowHeight - sectionTop) / (windowHeight + sectionHeight));
          // Use smaller parallax speed on mobile (max-width: 480px)
          const isMobile = window.innerWidth <= 480;
          const parallaxSpeed = isMobile ? 60 : 200; // Maximum pixels to move
          const translateX = scrollProgress * parallaxSpeed;
          indicator.style.transform = `translateX(${translateX}px)`;
        } else if (sectionTop >= windowHeight) {
          // Reset position when section is above viewport
          indicator.style.transform = `translateX(0px)`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="product-page">
      <section
        className="hero"
      >
        <div className="container">

          <label>Write</label>
          <h1 className="hero-title title-wide">Bring clarity to every page </h1>
          <p className="hero-subtitle subtitle-medium">
            An intiutive and professional writing experience, with industry standard formatting, timelines, shortcuts, versioning and much more.
          </p>
        </div>
        <div className="paper paper-4"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
        <div className="paper paper-5"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
        <div className="paper paper-6"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
      </section>

      <UIScript
        selectedTab={activeTab}
        onTabChange={setActiveTab}
        pageClassName="ui-script-product-write"
        scriptContent={
          <>
            <div className="script-action">Michael’s question is left unanswered as their whirlwind of a brother crashes the party. JACK, mid 30’s, handsome and dressed in an expensive suit.</div>
            <div className="script-character">JACK</div>
            <div className="script-dialogue">Sorry I'm late. I was with a client.</div>
            <div className="script-action">He kisses the top of Cara’s head and high-fives Michael, before taking a seat.</div>
            <div className="script-character">CARA</div>
            <div className="script-dialogue">You were with a client or were you the client?</div>
            <div className="script-action">Jack folds a napkin in his lap and claps back:</div>
            <div className="script-character">JACK</div>
            <div className="script-dialogue">Good one. Hey- how’s your marriage?</div>
            <div className="script-character">CARA</div>
            <div className="script-parenthetical">(fakes a smile)</div>
            <div className="script-dialogue">Great. Never been better.</div>
            <div className="script-action">Jack grabs an olive and leans back in the chair.</div>
            <div className="script-character">JACk</div>
            <div className="script-parenthetical">(talks with his mouth full)</div>
            <div className="script-dialogue">So -- how does the old man plan to divide his fundings?</div>
            <div className="script-action">He spots a waiter and waves him over.</div>
            <div className="script-character">JACK</div>
            <div className="script-dialogue">Can we get a bottle of Louis Roederer? Thank you.</div>
            <div className="script-character">CARA</div>
            <div className="script-dialogue">Champagne?</div>
            <div className="script-action">Jack nods and wipes his hands together in excitement. He’s ready to listen.</div>
            <div className="script-character">CARA</div>
            <div className="script-dialogue">We're going over dad's will and you’re ordering champagne?</div>
          </>
        }
      />
      <section className="section-marg two-column-section mt-112">
        <div className="two-column-container">
          <div className="two-column-item">
            <h2 className="section-title">Industry standard formatting</h2>
            <p className="section-subtitle">Fiktion gives you industry standard formatting, with Courier Prime, correct spacing, sizing, line-height, etc. </p>
            <div className="grey">
              <div className="fake-script">
                <div className="script-slug">INT. CONFERENCE HALL - AFTERNOON</div>
                <div className="script-action">A SPOTLIGHT reveals PAUL WHITMAN on stage. Huge condensed letters with the word “REDUNDANT” towers behind him.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Yesterday you had a title, a desk, and a cheap mug telling you, you we’re doing great. Today, the corporate world tells you you’re redundant. Just like that. </div>
                <div className="script-action">Paul takes a beat. A line animates across the screen and strikes out “REDUNDANT”.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Well, I don’t believe in redundancy.
                </div>
              </div>
            </div>
          </div>
          <div className="two-column-divider"></div>
          <div className="two-column-item">
            <h2 className="section-title">Type at the speed of thought</h2>
            <p className="section-subtitle">Fiktion knows the next natural element in your script, but you can cycle through options, with the TAB key or menu. </p>
            <div className="writing-options grey items-center justify-center">
              <div className="fake-script">
                <div className="caret"></div>
                <div class="fake-ui writing"><div class="fake-ui-sidebar-item">Scene Heading</div><div class="fake-ui-sidebar-item selected">Action</div><div class="fake-ui-sidebar-item">Character</div><div class="fake-ui-sidebar-item">Parenthetical</div><div class="fake-ui-sidebar-item">Dialogue</div><div class="fake-ui-sidebar-item">Transition</div><div class="fake-ui-sidebar-item">Shot</div></div>
              </div>
              <img src={`${process.env.PUBLIC_URL}/images/character-bg.jpg`} alt="Export" className="section-image-large absolute" />
            </div>
          </div>
        </div>
      </section>
      <div className="divider"></div>
      <section className="section-marg writing-section">
        <div className="container">
          <h2 className="section-title wide-section-title">Glide through your screenplay in an instant</h2>
          <p className="section-subtitle">The story map allows you to move between scenes with ease, and keep track of your progress.</p>

          <div className="script-wrapper story-map-wrapper">
            <div className="fake-story-map">
              <div className="fake-story-map-line" ref={storyMapLineRef}>
                {storyMapDots.map((dot) => (
                  <span
                    key={dot.id}
                    className="story-map-dot"
                    style={{ left: `${dot.left}px` }}
                  />
                ))}
              </div>
              <div className="story-map-indicators">
                <div className="story-map-number">1</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">5</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">10</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">15</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">20</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">25</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">30</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">35</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">40</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">45</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">50</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">55</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
              </div>
              <div className="purple-indicator" ref={purpleIndicatorRef}></div>
            </div>
            <div
              className="section-image-large story-map-image"
              role="img"
              aria-label="Writing"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/story-map-2.jpg)` }}
            ></div>
            <div className="story-map-popout">
              <div className="story-map-popout-content">
                <div className="story-map-popout-content-item-title">EXT. OFFICE – NIGHT</div>
                <div className="story-map-popout-content-item-description">Fluorescent lights hum over abandoned desks and printers mid-task as if someone...</div>
              </div>
            </div>
          </div>
          <div className="section-features">
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 16.2499V8.5166" stroke="black" stroke-width="1.5" stroke-linecap="round" />
                  <path d="M7 16.25V1.75" stroke="black" stroke-width="1.5" stroke-linecap="round" />
                  <path d="M11 16.25V11.4167" stroke="black" stroke-width="1.5" stroke-linecap="round" />
                  <path d="M15 16.25V6.58337" stroke="black" stroke-width="1.5" stroke-linecap="round" />
                </svg>
                <p>Script report</p>
              </div>
              <div className="section-feature-text">
                <p>View stats like character and word count, diversity, and most used words.</p>
              </div>
            </div>
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2H6C3.79086 2 2 3.79086 2 6V12C2 14.2091 3.79086 16 6 16H12C14.2091 16 16 14.2091 16 12V6C16 3.79086 14.2091 2 12 2Z" stroke="#040404" stroke-width="1.25" />
                  <path d="M9 12.25V5.75M9 5.75L6 8.75M9 5.75L12 8.75" stroke="#040404" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p>Upload XML or FDX</p>
              </div>
              <div className="section-feature-text">
                <p>Upload XML or FDX (Final Draft) files and continue your work in Fiktion.</p>
              </div>
            </div>
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.87468 7.4375H5.27051C4.16594 7.4375 3.27051 8.33293 3.27051 9.4375V14.8125C3.27051 15.9171 4.16594 16.8125 5.27051 16.8125H12.7289C13.8335 16.8125 14.7289 15.9171 14.7289 14.8125V9.4375C14.7289 8.33293 13.8335 7.4375 12.7289 7.4375H12.1247" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                  <path d="M9 10.5625V1.1875M9 1.1875L5.875 4.3125M9 1.1875L12.125 4.3125" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p>Export PDF or XML</p>
              </div>
              <div className="section-feature-text">
                <p>Export your script as PDF or XML files when you're done or for local backup.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="divider"></div>

      <section className="section-marg two-column-section mt-112">
        <div className="two-column-container">
          <div className="two-column-item">
            <h2 className="section-title">Script notes</h2>
            <p className="section-subtitle">Leave notes anywhere in the script, resolve them when you’re done, and stay focused on the rewrite. </p>
            <div className="grey">
              <div className="fake-script">
                <div className="script-slug">INT. CONFERENCE HALL - AFTERNOON</div>
                <div className="script-action script-highlight">A SPOTLIGHT reveals PAUL WHITMAN on stage. Huge condensed letters with the word “REDUNDANT” towers behind him.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Yesterday you had a title, a desk, and a cheap mug telling you, you we’re doing great. Today, the corporate world tells you you’re redundant. Just like that. </div>
                <div className="script-action">Paul takes a beat. A line animates across the screen and strikes out “REDUNDANT”.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Well, I don’t believe in redundancy.
                </div>
              </div>
              <div className="script-notes-popout">
                <div className="flex items-center">
                  <div className="caret"></div>
                  <p>Type your script note here...</p>
                </div>
                <div className="script-notes-submit">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.99992 12.8334V3.16675M7.99992 3.16675L3.83325 7.33342M7.99992 3.16675L12.1666 7.33342" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="two-column-divider"></div>
          <div className="two-column-item">
            <h2 className="section-title">Version history</h2>
            <p className="section-subtitle">Revisit every step of your script. Track drafts, jump back in time, and explore how your story found its shape.</p>
            <div className="grey items-center justify-center vh-container">
              <div className="version-history-wrapper">
                <div className="vh-label">
                  <p>Stats</p>
                </div>
                <div className="flex flex-row space-between">
                  <div className="flex flex-column w-50">
                    <p className="subdued-75">Total versions:</p>
                    <p className="subdued-75">Auto saves:</p>
                    <p className="subdued-75">Manual saves:</p>
                    <p className="subdued-75">Current:</p>
                  </div>
                  <div className="flex flex-column w-50">
                    <p>18</p>
                    <p>15</p>
                    <p>3</p>
                    <p>859 words, 4 pages</p>
                  </div>
                </div>
                <div className="vh-divider"></div>
                <div className="vh-label">
                  <p>Current version</p>
                </div>
                <div className="vh-block selected">
                  <p>Current</p>
                  <span>2.341 words, 9 pages, 8.043 characters</span>
                </div>
                <div className="vh-divider"></div>
                <div className="vh-label">
                  <p>Earlier versions</p>
                </div>
                <div className="vh-block">
                  <p>Autosave</p>
                  <span>2.341 words, 9 pages, 8.043 characters</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="divider"></div>
      <section className="section-marg writing-section">
        <div className="container">
          <h2 className="section-title">Find and replace</h2>
          <p className="section-subtitle">Make quick fixes without breaking your flow. Search your entire script and swap anything from character names to scene headers in seconds.</p>

          <div className="script-wrapper find-replace-wrapper">
            <div className="find-replace-container">
              <div className="fake-script-text">
                <div className="script-slug">INT. CONFERENCE HALL - AFTERNOON</div>
                <div className="script-action">A SPOTLIGHT reveals PAUL WHITMAN on stage. Huge condensed letters with the word “REDUNDANT” towers behind him.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Yesterday you had a title, a desk, and a cheap mug telling you, you we’re doing great. Today, the corporate world tells you you’re redundant. Just like that. </div>
                <div className="script-action">Paul takes a beat. A line animates across the screen and strikes out “REDUNDANT”.</div>
                <div className="script-character">PAUL</div>
                <div className="script-dialogue">Well, I don’t believe in redundancy. You can’t be redundant at being you.
                </div>
              </div>
              <div className="find-replace-box">
                <div className="flex flex-row space-between items-center gap-16 inputs">
                  <div className="flex flex-row items-center gap-12 w-50">
                    <p className="subdued-75 find-label">Find</p>
                    <input type="text" placeholder="Enter text to find..." />
                  </div>
                  <div className="flex flex-row items-center gap-12 w-50">
                    <p className="subdued-75">Replace</p>
                    <input type="text" placeholder="Enter text to replace..." />
                  </div>
                </div>
                <div className="find-replace-divider mobile-only"></div>
                <div className="flex flex-row items-center gap-12 mt-12 search-in-wrapper">
                  <p className="subdued-75">Search in:</p>
                  <div className="flex flex-row items-center gap-8">
                    <div className="flex flex-row items-center gap-8">
                      <div className="fake-checkbox"></div>
                      <p>Scene Heading</p>
                    </div>
                    <div className="flex flex-row items-center gap-8">
                      <div className="fake-checkbox"></div>
                      <p>Action</p>
                    </div>
                    <div className="flex flex-row items-center gap-8">
                      <div className="fake-checkbox"></div>
                      <p>Dialogue</p>
                    </div>
                  </div>
                </div>
                <div className="find-replace-divider"></div>
                <div className="find-replace-buttons flex flex-row flex-end gap-8">
                  <button className="btn btn-small btn-ghost">Find Next</button>
                  <button className="btn btn-small btn-ghost">Replace Next</button>
                  <button className="btn btn-small btn-primary">Replace All</button>
                </div>
              </div>
            </div>
            <div
              className="section-image-large"
              role="img"
              aria-label="Writing"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/find-replace.jpg)` }}
            ></div>
          </div>
        </div>
      </section>
      <div className="divider"></div>
      <ThinkDreamSection />

      <div className="divider"></div>

      <CTASection />
    </div>
  );
};

export default ProductWriting;
